import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import useLocale from '../hooks/useLocale';
import useUpdateBreadcrumbs from '../hooks/useUpdateBreadcrumbs';
import SimplePdfViewerIframe from '../components/SimplePdfViewerIframe';
import './ThesisManagement.css';
import { fetchPdfBlobUrl } from '../utils/pdf';

const ThesisManagement = () => {
  // was conference_id, section_id, thesis_id
  const { festschrift_id, section_id, article_id } = useParams();
  const festschrift_section_id = section_id;
  const { getCurrentLanguage, getLocalizedName } = useLocale();
  const lang = getCurrentLanguage();
  const [thesisData, setThesisData] = useState(null); // logically articleData
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const navigate = useNavigate();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const confNameFromQuery = searchParams.get('conferenceName') || '';
  const festCacheKey = `fest_name_${festschrift_id}`;
  const confNameCached = (() => {
    try {
      return sessionStorage.getItem(festCacheKey) || '';
    } catch {
      return '';
    }
  })();
  const confName = confNameFromQuery || confNameCached || '';

  const thesisTitle = searchParams.get('thesisTitle') || '';
  const sectionName = searchParams.get('sectionName') || '';

  useEffect(() => {
    if (!confNameFromQuery) return;
    try {
      sessionStorage.setItem(festCacheKey, confNameFromQuery);
    } catch {
      //TODO: add handling
    }
  }, [confNameFromQuery, festCacheKey]);

  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfErrorCode, setPdfErrorCode] = useState(null);

  // Подгружаем PDF текущей версии статьи
  useEffect(() => {
    let stop = false;
    (async () => {
      if (!article_id) return;
      const { url, errorCode } = await fetchPdfBlobUrl(article_id);
      if (!stop) {
        setPdfUrl(url);
        setPdfErrorCode(errorCode);
      }
    })();
    return () => {
      stop = true;
    };
  }, [article_id]);

  // Хлебные крошки: сборник → секция → статья
  const resetBreadcrumbs = useCallback(
    (search) => {
      const sp = new URLSearchParams(search);

      const confFromQuery = sp.get('conferenceName') || '';
      const confCached = (() => {
        try {
          return sessionStorage.getItem(`fest_name_${festschrift_id}`) || '';
        } catch {
          return '';
        }
      })();
      const conf = confFromQuery || confCached || '';

      const sect = sp.get('sectionName') || '';
      const title = sp.get('thesisTitle') || '';

      const encConf = encodeURIComponent(conf);
      const encSect = encodeURIComponent(sect);

      return [
        {
          label: conf,
          url: `/management/publishing/${festschrift_id}?conferenceName=${encConf}`,
        },
        {
          label: sect,
          url:
            `/management/publishing/${festschrift_id}/sections/${festschrift_section_id}/articles` +
            `?conferenceName=${encConf}&sectionName=${encSect}`,
        },
        { label: title },
      ];
    },
    [festschrift_id, festschrift_section_id],
  );

  useUpdateBreadcrumbs(resetBreadcrumbs);

  useEffect(() => {
    const fetchThesisData = async () => {
      setLoading(true);
      try {
        // was /management/conferences/{conference_id}/sections/{section_id}/thesises/{thesis_id}
        const response = await api.get(
          `/management/publishing/${festschrift_id}/sections/${festschrift_section_id}/articles/${article_id}`,
        );
        setThesisData(response.data);
        setError('');
      } catch (err) {
        console.error(err);
        setError(
          lang === 'ru' ? 'Ошибка при загрузке данных статьи' : 'Failed to load article data',
        );
      } finally {
        setLoading(false);
      }
    };
    fetchThesisData();
  }, [festschrift_id, festschrift_section_id, article_id, lang]);

  // Заголовок статьи
  const getThesisTitle = (titleObj) => {
    if (!titleObj) return '';
    return getLocalizedName(titleObj);
  };

  // Полное имя (автор или рецензент)
  const getFullName = (fullNameObj) => {
    if (!fullNameObj) return '';
    const namePart = fullNameObj.name ? getLocalizedName(fullNameObj.name) : '';
    const surnamePart = fullNameObj.surname ? getLocalizedName(fullNameObj.surname) : '';
    return `${namePart} ${surnamePart}`.trim();
  };

  if (loading) {
    return (
      <div className="loading-state">
        {lang === 'ru' ? 'Загрузка данных статьи...' : 'Loading article data...'}
      </div>
    );
  }

  if (error) {
    return <div className="error-state">{error}</div>;
  }

  const { title, author, ready_reviews = [], pending_tasks = [], rejected_tasks = [] } = thesisData;

  // Reviews in progress: started, but no score yet => status=in_progress.
  // Completed: approved/rejected/revision_required/finalized.
  const inProgressReviews = ready_reviews.filter(
    (r) => String(r?.status || '').toLowerCase() === 'in_progress',
  );

  const completedReviews = ready_reviews.filter((r) => {
    const st = String(r?.status || '').toLowerCase();
    return (
      st === 'approved' || st === 'rejected' || st === 'revision_required' || st === 'finalized'
    );
  });

  const goToReview = (reviewId) => {
    navigate(
      `/publishing/${festschrift_id}/review/${reviewId}` +
        `?conferenceName=${encodeURIComponent(confName)}` +
        `&sectionName=${encodeURIComponent(sectionName)}` +
        `&thesisTitle=${encodeURIComponent(thesisTitle)}` +
        `&editable=False&flow=management` +
        `&conference_id=${festschrift_id}&section_id=${festschrift_section_id}&thesis_id=${article_id}`,
    );
  };

  const reviewStatusRepresentation = (review) => {
    if (!review || review.status == null) return '';
    const st = String(review.status).toLowerCase();

    if (st === 'in_progress') return lang === 'ru' ? 'В процессе' : 'In progress';
    if (st === 'approved') return lang === 'ru' ? 'Одобрено' : 'Approved';
    if (st === 'rejected') return lang === 'ru' ? 'Отклонено' : 'Rejected';
    if (st === 'revision_required')
      return lang === 'ru' ? 'Требуется доработка' : 'Revision required';
    if (st === 'finalized') return lang === 'ru' ? 'Завершено' : 'Finalized';

    // Keep visible if backend ever sends extra states (e.g. accepted)
    if (st === 'accepted') return lang === 'ru' ? 'Принял' : 'Accepted';
    if (st === 'pending') return lang === 'ru' ? 'Приглашён' : 'Invited';

    return review.status;
  };

  const isLocked = !!thesisData?.is_locked;

  const handleDeleteArticle = async () => {
    if (deleting) return;
    if (!isLocked) return;

    const ok = window.confirm(
      lang === 'ru'
        ? 'Удалить статью? Это действие необратимо.'
        : 'Delete this article? This action cannot be undone.',
    );
    if (!ok) return;

    setDeleting(true);
    setDeleteError('');

    try {
      await api.delete(
        `/management/publishing/${festschrift_id}/sections/${festschrift_section_id}/articles/${article_id}/delete`,
      );
      navigate(-1);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        (lang === 'ru' ? 'Не удалось удалить статью' : 'Failed to delete article');
      setDeleteError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setDeleting(false);
    }
  };

  return (
    <div className="thesis-management-layer">
      <div className="thesis-management-container">
        <div className="thesis-header">
          <p className="thesis-management-title">{getThesisTitle(title)}</p>

          <div className="thesis-header-actions">
            <button
              className={`delete-article-btn ${isLocked ? '' : 'disabled'}`}
              onClick={handleDeleteArticle}
              title={
                isLocked
                  ? lang === 'ru'
                    ? 'Удалить статью'
                    : 'Delete article'
                  : lang === 'ru'
                    ? 'Удаление доступно только после блокировки статьи'
                    : 'Deletion is available only after the article is locked'
              }
              aria-disabled={!isLocked}
            >
              {deleting
                ? lang === 'ru'
                  ? 'Удаляем...'
                  : 'Deleting...'
                : lang === 'ru'
                  ? 'Удалить'
                  : 'Delete'}
            </button>
          </div>
        </div>

        {deleteError ? <div className="error-state">{deleteError}</div> : null}

        <div className="thesis-author-card">
          <h2>{lang === 'ru' ? 'Информация об авторе' : 'Author information'}</h2>
          <p>
            <strong>{lang === 'ru' ? 'ФИО:' : 'Full name:'}</strong> {getFullName(author.full_name)}
          </p>
          <p>
            <strong>Email:</strong> {author.email}
          </p>
          {author.phone && (
            <p>
              <strong>{lang === 'ru' ? 'Телефон:' : 'Phone:'}</strong> {author.phone}
            </p>
          )}
          {author.orcid && (
            <p>
              <strong>ORCID:</strong> {author.orcid}
            </p>
          )}
          {author.website && (
            <p>
              <strong>{lang === 'ru' ? 'Веб-сайт:' : 'Website:'}</strong>{' '}
              <a href={author.website} target="_blank" rel="noopener noreferrer">
                {author.website}
              </a>
            </p>
          )}
        </div>

        <div className="reviews-section">
          <h2>{lang === 'ru' ? 'Рецензии в процессе' : 'Reviews in progress'}</h2>
          {inProgressReviews.length > 0 ? (
            <div className="table-container">
              <table className="reviews-table">
                <thead>
                  <tr>
                    <th>{lang === 'ru' ? 'Рецензент' : 'Reviewer'}</th>
                    <th>Email</th>
                    <th>{lang === 'ru' ? 'Статус' : 'Status'}</th>
                  </tr>
                </thead>
                <tbody>
                  {inProgressReviews.map((review) => (
                    <tr key={review.review_id} onClick={() => goToReview(review.review_id)}>
                      <td>{getFullName(review.reviewer_full_name)}</td>
                      <td>{review.reviewer_email}</td>
                      <td>{reviewStatusRepresentation(review)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>{lang === 'ru' ? 'Нет рецензий в процессе' : 'No reviews in progress'}</p>
          )}
        </div>

        <div className="reviews-section">
          <h2>{lang === 'ru' ? 'Готовые рецензии' : 'Completed reviews'}</h2>
          {completedReviews.length > 0 ? (
            <div className="table-container">
              <table className="reviews-table">
                <thead>
                  <tr>
                    <th>{lang === 'ru' ? 'Рецензент' : 'Reviewer'}</th>
                    <th>Email</th>
                    <th>{lang === 'ru' ? 'Статус' : 'Status'}</th>
                  </tr>
                </thead>
                <tbody>
                  {completedReviews.map((review) => (
                    <tr key={review.review_id} onClick={() => goToReview(review.review_id)}>
                      <td>{getFullName(review.reviewer_full_name)}</td>
                      <td>{review.reviewer_email}</td>
                      <td>{reviewStatusRepresentation(review)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>{lang === 'ru' ? 'Нет готовых рецензий' : 'No completed reviews'}</p>
          )}
        </div>

        <div className="tasks-section">
          <h2>{lang === 'ru' ? 'Ожидающие задачи' : 'Pending tasks'}</h2>
          {pending_tasks.length > 0 ? (
            <div className="table-container">
              <table className="tasks-table">
                <thead>
                  <tr>
                    <th>{lang === 'ru' ? 'Рецензент' : 'Reviewer'}</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {pending_tasks.map((task) => (
                    <tr key={task.reviewer_id} onClick={() => alert('Переход к задаче')}>
                      <td>{getFullName(task.reviewer_full_name)}</td>
                      <td>{task.reviewer_email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>{lang === 'ru' ? 'Нет ожидающих задач' : 'No pending tasks'}</p>
          )}
        </div>

        <div className="tasks-section">
          <h2>{lang === 'ru' ? 'Отклонённые задачи' : 'Rejected tasks'}</h2>
          {rejected_tasks.length > 0 ? (
            <div className="table-container">
              <table className="tasks-table">
                <thead>
                  <tr>
                    <th>{lang === 'ru' ? 'Рецензент' : 'Reviewer'}</th>
                    <th>Email</th>
                    <th>{lang === 'ru' ? 'Причина отказа' : 'Rejection reason'}</th>
                  </tr>
                </thead>
                <tbody>
                  {rejected_tasks.map((task) => (
                    <tr key={task.reviewer_id} onClick={() => alert('Переход к задаче')}>
                      <td>{getFullName(task.reviewer_full_name)}</td>
                      <td>{task.reviewer_email}</td>
                      <td>{task.reject_reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>{lang === 'ru' ? 'Нет отклонённых задач' : 'No rejected tasks'}</p>
          )}
        </div>

        <div className="thesis-text-section">
          <h2>{lang === 'ru' ? 'Текст статьи' : 'Article text'}</h2>
          {pdfUrl ? (
            <SimplePdfViewerIframe src={pdfUrl} />
          ) : (
            <div style={{ padding: 16 }}>
              <div style={{ marginTop: 8, color: '#b00' }}>
                {pdfErrorCode === 403 &&
                  (lang === 'ru'
                    ? 'У вас нет доступа к PDF этой работы.'
                    : "You don't have access to this work's PDF.")}
                {pdfErrorCode === 404 &&
                  (lang === 'ru'
                    ? 'PDF ещё не загружен или находится в процессе конвертации.'
                    : 'The PDF has not been uploaded yet or is still being converted.')}
                {pdfErrorCode &&
                  pdfErrorCode !== 403 &&
                  pdfErrorCode !== 404 &&
                  (lang === 'ru'
                    ? `Не удалось загрузить PDF (ошибка ${String(pdfErrorCode)}).`
                    : `Failed to load PDF (error ${String(pdfErrorCode)}).`)}
                {!pdfErrorCode &&
                  !pdfUrl &&
                  (lang === 'ru' ? 'PDF недоступен.' : 'PDF is not available.')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThesisManagement;
